import React from 'react';
import { CheckSquare, ExternalLink, FileText, Image } from 'lucide-react';
import type { LstTemplate } from '../types';

interface SwiStandardViewProps {
  template: LstTemplate;
}

function isImageUrl(url: string): boolean {
  return /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url);
}

/** Bloco 1 — exibe o padrão configurado (texto, POP ou questionário). */
export const SwiStandardView: React.FC<SwiStandardViewProps> = ({ template }) => {
  const text = template.swi_text?.trim() || template.description?.trim() || '';
  const docUrl = template.swi_doc_url?.trim() || '';
  const checklist = template.swi_checklist ?? [];
  const mode = template.swi_mode ?? 'text';

  const hasText = text.length > 0;
  const hasDoc = docUrl.length > 0;
  const hasChecklist = checklist.length > 0;

  if (!hasText && !hasDoc && !hasChecklist) {
    return (
      <p className="text-[12px] italic text-slate-400">
        Nenhuma Instrução de Trabalho cadastrada. Configure em Configuração → Cadastro de Rotinas.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* Modo principal */}
      {mode === 'text' && hasText && (
        <div
          className="prose prose-sm max-w-none text-[12px] leading-relaxed text-slate-600 dark:text-slate-300 [&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:list-disc [&_ul]:pl-4"
          dangerouslySetInnerHTML={{ __html: text }}
        />
      )}

      {mode === 'document' && hasDoc && (
        <div>
          <a
            href={docUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            <ExternalLink size={13} /> Abrir POP visual
          </a>
          {isImageUrl(docUrl) && (
            <img
              src={docUrl}
              alt="POP visual"
              className="mt-2 max-h-40 w-full rounded border border-slate-200 object-contain dark:border-slate-700"
            />
          )}
        </div>
      )}

      {mode === 'checklist' && hasChecklist && (
        <ul className="space-y-1">
          {checklist.map((item, idx) => (
            <li key={item.id} className="flex items-start gap-2 text-[12px] text-slate-600 dark:text-slate-300">
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-slate-300 text-[9px] font-bold text-slate-400 dark:border-slate-600">
                {idx + 1}
              </span>
              {item.text}
            </li>
          ))}
        </ul>
      )}

      {/* Conteúdo complementar dos outros modos */}
      {mode !== 'text' && hasText && (
        <div className="rounded border border-slate-100 bg-slate-50/50 px-2 py-1.5 dark:border-slate-800 dark:bg-slate-900/30">
          <span className="mb-1 flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">
            <FileText size={9} /> Texto complementar
          </span>
          <div
            className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400 [&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:list-disc [&_ul]:pl-4"
            dangerouslySetInnerHTML={{ __html: text }}
          />
        </div>
      )}
      {mode !== 'document' && hasDoc && (
        <a
          href={docUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          <Image size={11} /> Ver POP anexado
        </a>
      )}
      {mode !== 'checklist' && hasChecklist && (
        <div className="rounded border border-slate-100 bg-slate-50/50 px-2 py-1.5 dark:border-slate-800 dark:bg-slate-900/30">
          <span className="mb-1 flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">
            <CheckSquare size={9} /> Verificação interna ({checklist.length} passos)
          </span>
          <ul className="space-y-0.5">
            {checklist.map((item) => (
              <li key={item.id} className="text-[11px] text-slate-500 dark:text-slate-400">· {item.text}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SwiStandardView;
